import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, CircularProgress, Alert,
  Tabs, Tab, Paper, InputAdornment, Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import adminApi from "../api/adminApi";

// Load the hardcoded defaults from i18n so the editor always shows all keys
import i18n from "../../i18n";

const getDefaultKeys = (lang) => {
  const resources = i18n.getResourceBundle(lang, "translation");
  return resources || {};
};

const TranslationEditor = () => {
  const [tab, setTab] = useState(0);
  const [keys, setKeys] = useState({ en: {}, pt: {} });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const lang = tab === 0 ? "en" : "pt";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const defaults = { en: getDefaultKeys("en"), pt: getDefaultKeys("pt") };
      try {
        const [enRes, ptRes] = await Promise.all([
          adminApi.get("/translations/en"),
          adminApi.get("/translations/pt"),
        ]);
        // Merge: DB overrides on top of defaults
        setKeys({
          en: { ...defaults.en, ...enRes.data },
          pt: { ...defaults.pt, ...ptRes.data },
        });
      } catch {
        setKeys(defaults);
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleChange = (key, value) => {
    setKeys((prev) => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await adminApi.put(`/translations/${lang}`, { keys: keys[lang] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const visibleKeys = Object.entries(keys[lang]).filter(
    ([k, v]) => !search || k.toLowerCase().includes(search.toLowerCase()) || v?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold" color="#1C2541">Translation Editor</Typography>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving} sx={{ backgroundColor: "#1C2541" }}>
          Save {lang.toUpperCase()}
        </Button>
      </Box>

      {saved && <Alert severity="success" sx={{ mb: 2 }}>Translations saved successfully</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="English" />
        <Tab label="Portuguese" />
      </Tabs>

      <TextField
        placeholder="Search keys or values..."
        fullWidth
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
      />

      <Paper elevation={1} sx={{ borderRadius: 3, overflow: "hidden" }}>
        {visibleKeys.map(([key, value], i) => (
          <Box key={key}>
            {i > 0 && <Divider />}
            <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "flex-start", "&:hover": { backgroundColor: "#fafafa" } }}>
              <Typography variant="body2" fontFamily="monospace" color="#1C2541" sx={{ minWidth: 220, pt: 1, wordBreak: "break-all" }}>
                {key}
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                value={value || ""}
                onChange={(e) => handleChange(key, e.target.value)}
              />
            </Box>
          </Box>
        ))}
        {visibleKeys.length === 0 && (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">No keys match your search</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TranslationEditor;
